import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  List,
  ListItem,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useUser } from '../../hooks/useUser';

// Define the API order interface based on the provided data structure
interface OrderItem {
  title: string;
  description: string;
  food_type: string;
  price: number;
  quantity: number;
  in_stock: boolean;
  modifier_groups?: {
    title: string;
    ref_id: string;
    type: string;
    selected_option: {
      title: string;
      ref_id: string;
      price: number;
      in_stock: boolean;
    };
  }[];
}

interface OrderCategory {
  title: string;
  description: string;
  items: OrderItem[];
}

interface CustomerInfo {
  name: string;
  contact_number: string;
  country_code: string;
  customer_email: string;
  full_address: string;
  locality: string;
  payment_method_preference: string;
  preferred_time_for_delivery_pickup: string;
}

interface PaymentInfo {
  ref_id: string;
  status: string;
  payment_method: string | null;
  transaction_id: string;
  amount: number;
}

interface ApiOrder {
  ref_id: string;
  user_ref_id: string;
  status: string;
  total_price: number;
  total_tax: number;
  created_at: string;
  customer_info: CustomerInfo;
  categories: OrderCategory[];
  payment: PaymentInfo;
  id: number;
}

interface OrderDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  order: ApiOrder | null;
  getPaymentStatusColor: (status: string) => string;
  getPaymentTextColor: (status: string) => string;
  getOrderStatusColor: (status: string) => string;
  getOrderTextColor: (status: string) => string;
}

const OrderDetailDrawer: React.FC<OrderDetailDrawerProps> = ({
  open,
  onClose,
  order,
  getPaymentStatusColor,
  getPaymentTextColor,
}) => {
  const { user } = useUser();

  if (!order) return null;
  // ts-expect-error

  // Format the order status text (convert snake_case to Title Case)
  const formatStatusText = (status: string) => {
    if (status) {
      return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return '';
  };

  // Format date
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'UTC',
      })
      .replace('at', ',')
      .replace(',', '');
  };

  const showInr =
    user?.tenantName === 'cure_food' || user?.tenantName === 'nik_bakers';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 450 },
          py: 1,
        },
      }}
    >
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            // alignItems: 'center',
            mb: 2,
            gap: 2,
            px: '16px',
          }}
        >
          <IconButton onClick={onClose} size="small">
            <X />
          </IconButton>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography fontWeight="600">Order #{order.id}</Typography>
            <Typography variant="body2" color="text.secondary">
              Order Created at {formatDate(order.created_at)}
            </Typography>
          </Box>
        </Box>

        {/* <Divider sx={{ mb: 3 }} /> */}

        {/* Customer Information */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight="600"
            sx={{
              backgroundColor: '#F5F5F5',
              padding: 1,
              borderRadius: 1,
              px: 3,
            }}
          >
            Customer Details
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: '#fff',
              borderRadius: 2,
              px: 3,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="14px"
                >
                  Name
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="400"
                  fontSize="14px"
                  color="text.primary"
                >
                  {order.customer_info?.name || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="14px"
                >
                  Phone
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="400"
                  fontSize="14px"
                  color="text.primary"
                >
                  {order?.customer_info?.contact_number &&
                    order?.customer_info?.country_code !== 'N/A' &&
                    order?.customer_info?.country_code + ' '}
                  {order?.customer_info?.contact_number || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="14px"
                >
                  Email
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="400"
                  fontSize="14px"
                  color="text.primary"
                >
                  {order?.customer_info?.customer_email || 'N/A'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="14px"
                >
                  Address
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="400"
                  fontSize="14px"
                  color="text.primary"
                >
                  {order?.customer_info?.full_address || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Payment Information */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            fontWeight="600"
            sx={{
              backgroundColor: '#F5F5F5',
              padding: 1,
              borderRadius: 1,
              px: 3,
            }}
          >
            Payment Information
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: '#fff',
              borderRadius: 2,
              px: 3,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="14px"
                >
                  Payment Method
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="400"
                  fontSize="14px"
                  color="text.primary"
                >
                  {formatStatusText(
                    order?.payment?.payment_method || 'Unknown'
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="14px"
                >
                  Payment Status
                </Typography>
                <Box
                  sx={{
                    backgroundColor: getPaymentStatusColor(
                      order?.payment?.status
                    ),
                    color: getPaymentTextColor(order?.payment?.status),
                    width: 'auto',
                    minWidth: 78,
                    maxWidth: 130,
                    height: 24,
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    fontWeight: '400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {order?.payment?.status
                    .split('_')
                    .map(
                      (word: string) =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                    )
                    .join(' ')}
                </Box>
              </Box>
              {order?.payment?.transaction_id && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight="400"
                    fontSize="14px"
                  >
                    Transaction ID
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="400"
                    fontSize="14px"
                    color="text.primary"
                  >
                    {order?.payment?.transaction_id}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="400"
                  fontSize="14px"
                >
                  Amount
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="400"
                  fontSize="14px"
                  color="text.primary"
                >
                  {order?.payment?.amount?.toFixed(2)} {showInr ? 'Rs.' : '$'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>

        {/* Order Summary */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="subtitle1"
            fontWeight="600"
            sx={{
              backgroundColor: '#F5F5F5',
              padding: 1,
              borderRadius: 1,
              px: 3,
            }}
          >
            Order Summary
          </Typography>

          {order?.categories?.map((category, categoryIndex) => (
            <Box key={categoryIndex}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: '#FFF',
                  borderRadius: 2,
                  px: 3,
                }}
              >
                <List disablePadding>
                  {category.items.map((item, itemIndex) => (
                    <Box key={itemIndex}>
                      <ListItem
                        disablePadding
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          py: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center',
                            borderBottom: '1px solid #E0E0E0',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              marginBottom: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="500"
                              fontSize="16px"
                              color="text.primary"
                            >
                              {category.title}
                            </Typography>
                            <Typography
                              variant="body1"
                              fontWeight="400"
                              fontSize="14px"
                              color="text.secondary"
                            >
                              {item.quantity}x {item.title}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body1"
                            fontWeight="400"
                            fontSize="14px"
                            color="text.primary"
                          >
                            {item.price.toFixed(2)} {showInr ? 'Rs.' : '$'}
                          </Typography>
                        </Box>

                        {item.modifier_groups &&
                          item.modifier_groups.length > 0 && (
                            <Box
                              sx={{
                                width: '100%',
                                mt: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                borderBottom: '1px dashed #E0E0E0',
                              }}
                            >
                              {item.modifier_groups.map((group, groupIndex) => (
                                <Box
                                  key={groupIndex}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontSize="14px"
                                  >
                                    {group.title}: {group.selected_option.title}
                                  </Typography>
                                  {group.selected_option.price > 0 && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      fontSize="14px"
                                    >
                                      +{group.selected_option.price.toFixed(2)}{' '}
                                      {showInr ? 'Rs.' : '$'}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                      </ListItem>
                      {itemIndex < category.items.length - 1 && (
                        <Divider sx={{ my: 1 }} />
                      )}
                    </Box>
                  ))}
                </List>
              </Paper>
            </Box>
          ))}
          {/* Order Total */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#fff',
              borderRadius: 2,
              px: 2,
              mb: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography variant="body1" fontWeight="400" fontSize="14px">
                Total Tax
              </Typography>
              <Typography variant="body1" fontWeight="400" fontSize="14px">
                {(order?.total_tax || 0).toFixed(2)} {showInr ? 'Rs.' : '$'}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                borderTop: '1px solid #E0E0E0',
                pt: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight="600">
                Total Amount
              </Typography>
              <Typography variant="subtitle1" fontWeight="600">
                {order?.total_price?.toFixed(2)} {showInr ? 'Rs.' : '$'}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Drawer>
  );
};

export default OrderDetailDrawer;
